import { getTargetDefault } from '@nohara-dxdev/feature-flags';
import {
  constructOrgTargetId,
  constructOrgUserTargetId,
  FEATURE_FLAGS,
  type FeatureFlagKey,
  TARGET_ORG,
  TARGET_ORG_USER,
} from '@nohara-dxdev/mt-common';
import { useMemo } from 'react';

import { useUserFeatureFlags } from '../api/get-user-feature-flags-admin';

export type Effective = 'on' | 'off-org' | 'off-user';

export interface UserFlagRow {
  flagKey: FeatureFlagKey;
  orgEnabled: boolean;
  userEnabled: boolean;
  effective: Effective;
}

export interface UseUserFeatureFlagAdminStateResult {
  flags: UserFlagRow[];
  isLoading: boolean;
  isError: boolean;
}

export const useUserFeatureFlagAdminState = (
  orgId: string | null | undefined,
  userId: string | null | undefined,
): UseUserFeatureFlagAdminStateResult => {
  const query = useUserFeatureFlags({ orgId, userId });

  const flags: UserFlagRow[] = useMemo(() => {
    if (!query.data || !orgId || !userId) return [];
    const orgTargetId = constructOrgTargetId(orgId);
    const orgUserId = constructOrgUserTargetId(orgId, userId);
    return (Object.keys(FEATURE_FLAGS) as FeatureFlagKey[]).map((flagKey) => {
      const def = FEATURE_FLAGS[flagKey];
      const states = query.data.flags.filter((s) => s.flagKey === flagKey);
      const orgRow = states.find((s) => s.targetType === TARGET_ORG && s.targetId === orgTargetId);
      const userRow = states.find(
        (s) => s.targetType === TARGET_ORG_USER && s.targetId === orgUserId,
      );
      const orgEnabled = orgRow?.enabled ?? getTargetDefault(def.targets, TARGET_ORG);
      const userEnabled = userRow?.enabled ?? getTargetDefault(def.targets, TARGET_ORG_USER);
      const effective: Effective = !orgEnabled ? 'off-org' : !userEnabled ? 'off-user' : 'on';
      return { flagKey, orgEnabled, userEnabled, effective };
    });
  }, [query.data, orgId, userId]);

  return { flags, isLoading: query.isLoading, isError: query.isError };
};
